import { mdiPlusCircleOutline } from '@mdi/js'
import { Icon } from '@mdi/react'
import CardList, { CARD_LIST_HEIGHT_SMALL, ITEM_WIDTH_SMALL } from 'components/CardList'
import { BucketCard, SocialProfileCard } from 'components/Cards'
import FetchPostDestinations from 'components/Fetch/FetchPostDestinations'
import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { NavLink, useNavigate } from 'react-router'
import { renderLoadingCards } from 'routes/find/routes/home/components/utils'
import { profilesByPpidSelector } from 'services/destinations'
import { setSelectedProfiles } from 'services/post'
import { getPostsCount } from 'services/post/actions'
import { bucketsSelector, postsCountSelector } from 'services/post/selectors'
import { Bucket, PostDestination } from 'shared'
import { StoreThunkDispatch } from 'store/state'
import styles from './ManagePostsCarousel.pcss'
const css = require('styles/variables')

export function ManagePostsCarousel() {
  const dispatch = useDispatch<StoreThunkDispatch>()
  const navigate = useNavigate()
  const profiles = useSelector(profilesByPpidSelector)
  const buckets = useSelector(bucketsSelector)
  const postsCount = useSelector(postsCountSelector)

  const [loading, setLoading] = useState(true)

  const stopLoading = () => setLoading(false)

  useEffect(() => {
    dispatch(getPostsCount())
  }, [dispatch])

  const bucketsWithProfiles: Array<Bucket & { profiles: PostDestination[] }> = buckets.map(bucket => ({
    ...bucket,
    profiles: bucket.ppids.map(ppid => profiles[ppid])
  }))

  const onProfileClick = (profile: PostDestination) => {
    dispatch(setSelectedProfiles({ profiles: [profile] }))
    navigate('/posts/planned/all')
  }

  const onBucketClick = (bucket: Bucket & { profiles: PostDestination[] }) => {
    navigate(`/posts/buckets/${bucket.id}/posts`)
  }

  type CarouselItem =
    | { kind: 'profile', postsCount: number, profile: PostDestination }
    | { kind: 'bucket', postsCount: number, bucket: Bucket & { profiles: PostDestination[] } }

  const items: CarouselItem[] = [
    ...Object.values(profiles).filter(p => p.connected).map<CarouselItem>(profile => ({
      kind: 'profile',
      postsCount: postsCount[profile.ppid] || 0,
      profile
    })),
    ...bucketsWithProfiles.map<CarouselItem>(bucket => ({
      kind: 'bucket',
      postsCount: bucket.postsCount,
      bucket
    }))
  ].sort((a, b) => b.postsCount - a.postsCount)

  return (
    <div className={styles.container}>
      <h2>Manage your posts and buckets 🪣</h2>
      {!loading && items.length === 0 ? (
        <div className={styles.btnAddProfilesContainer}>
          <NavLink to="/settings/social-networks" className={styles.btnAddProfiles}>
            <Icon path={mdiPlusCircleOutline} size="120px" color={css['--light-gray']} />
            <span className={styles.label}>Connect an account</span>
          </NavLink>
        </div>
      ) : (
        <CardList
          itemWidth={ITEM_WIDTH_SMALL}
          listHeight={CARD_LIST_HEIGHT_SMALL}
          listItemClassName={styles.li}
        >
          {items.length === 0 ? renderLoadingCards(undefined, true, true, true) : items.map(item => item.kind === 'profile' ? (
            <SocialProfileCard
              postsCount={item.postsCount}
              profile={item.profile}
              key={`profile-${item.profile.ppid}`}
              onClick={onProfileClick}
            />
          ) : (
            <BucketCard
              bucket={item.bucket}
              key={`bucket-${item.bucket.id}`}
              onClick={onBucketClick}
            />
          ))}
        </CardList>
      )}
      <FetchPostDestinations force={false} onFailed={stopLoading} onFetched={stopLoading} />
    </div>
  )
}
